import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { BRANCHES, PROGRAMMES, STAFF } from "../../data/mockData";
import { highlevelPaymentsService } from "../../services/highlevelPaymentsService";
import { Palette, MapPin, GraduationCap, Users, Plug, CheckCircle2, CircleDashed } from "lucide-react";

const SWATCHES = ["hsl(215 84% 22%)", "hsl(188 62% 33%)", "hsl(199 78% 38%)", "hsl(262 50% 40%)", "hsl(16 84% 46%)", "hsl(150 45% 30%)"];

export default function SettingsPage() {
  const { vertical, config } = useOutletContext();
  const [brandName, setBrandName] = useState(config.subheadline.split(" — ")[0]);
  const [colour, setColour] = useState(SWATCHES[0]);

  useEffect(() => {
    setBrandName(config.subheadline.split(" — ")[0]);
  }, [config]);

  const save = () => toast.success("Brand settings saved (demo) — your white-labelled portal would update instantly");

  return (
    <div className="space-y-4 animate-fade-up max-w-5xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Brand */}
        <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
          <CardContent className="p-5">
            <p className="font-semibold text-sm text-foreground mb-4 flex items-center gap-1.5"><Palette className="h-4 w-4 text-accent" /> Brand</p>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="brand_name">Centre / clinic name</Label>
                <Input id="brand_name" className="h-11 rounded-xl" value={brandName} onChange={(e) => setBrandName(e.target.value)} data-testid="settings-brand-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Primary colour</Label>
                <div className="flex gap-2">
                  {SWATCHES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setColour(s)}
                      className={`h-9 w-9 rounded-xl transition-transform ${colour === s ? "ring-2 ring-offset-2 ring-[hsl(var(--ring))] scale-105" : "hover:scale-105"}`}
                      style={{ background: s }}
                      data-testid={`settings-colour-swatch-${s.replace(/[^0-9]/g, "")}`}
                      aria-label={`Colour ${s}`}
                    />
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Logo</Label>
                <div className="border-2 border-dashed border-border rounded-xl p-6 text-center text-xs text-muted-foreground">
                  Drop your logo here — shown on parent-facing booking pages and messages (demo)
                </div>
              </div>
              <Button className="rounded-xl w-full" onClick={save} data-testid="settings-save-brand-button">Save brand settings</Button>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {/* Branches */}
          <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-sm text-foreground flex items-center gap-1.5"><MapPin className="h-4 w-4 text-accent" /> {config.branchLabel}s</p>
                <Button size="sm" variant="secondary" className="rounded-lg border border-border text-xs h-8" onClick={() => toast.success(`Add ${config.branchLabel.toLowerCase()} dialog would open (demo)`)} data-testid="settings-add-branch-button">Add</Button>
              </div>
              <div className="space-y-2" data-testid="settings-branches-list">
                {vertical === "enrichment"
                  ? BRANCHES.map((b) => (
                      <div key={b.id} className="bg-secondary rounded-xl px-3.5 py-2.5">
                        <p className="text-sm font-medium text-foreground">{b.name}</p>
                        <p className="text-[11px] text-muted-foreground">{b.address} · {b.phone}</p>
                      </div>
                    ))
                  : config.branches.map((b) => (
                      <div key={b} className="bg-secondary rounded-xl px-3.5 py-2.5">
                        <p className="text-sm font-medium text-foreground">{b}</p>
                      </div>
                    ))}
              </div>
            </CardContent>
          </Card>

          {/* Staff */}
          <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-sm text-foreground flex items-center gap-1.5"><Users className="h-4 w-4 text-accent" /> Team</p>
                <Button size="sm" variant="secondary" className="rounded-lg border border-border text-xs h-8" onClick={() => toast.success("Invite team member dialog would open (demo)")} data-testid="settings-add-staff-button">Invite</Button>
              </div>
              <div className="space-y-2" data-testid="settings-staff-list">
                {(vertical === "enrichment" ? STAFF : config.staff).map((s) => (
                  <div key={s.id} className="flex items-center gap-3 bg-secondary rounded-xl px-3.5 py-2.5">
                    <span className="h-8 w-8 rounded-full bg-[hsl(199_78%_92%)] text-[hsl(215_84%_22%)] text-xs font-semibold flex items-center justify-center shrink-0">{s.initials}</span>
                    <div>
                      <p className="text-sm font-medium text-foreground">{s.name}</p>
                      <p className="text-[11px] text-muted-foreground">{s.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Programmes */}
      <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-sm text-foreground flex items-center gap-1.5"><GraduationCap className="h-4 w-4 text-accent" /> {config.programmeLabel}s</p>
            <Button size="sm" variant="secondary" className="rounded-lg border border-border text-xs h-8" onClick={() => toast.success(`Add ${config.programmeLabel.toLowerCase()} dialog would open (demo)`)} data-testid="settings-add-programme-button">Add</Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5" data-testid="settings-programmes-list">
            {vertical === "enrichment"
              ? PROGRAMMES.map((p) => (
                  <div key={p.id} className="bg-secondary rounded-xl px-3.5 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-foreground">{p.name}</p>
                      <Badge variant="secondary" className={`text-[9px] ${p.colour}`}>{p.levels}</Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">{p.weeklyClasses} classes/week · max {p.capacity} per class</p>
                  </div>
                ))
              : config.programmes.map((p) => (
                  <div key={p} className="bg-secondary rounded-xl px-3.5 py-3">
                    <p className="text-sm font-medium text-foreground">{p}</p>
                  </div>
                ))}
          </div>
        </CardContent>
      </Card>

      {/* Integrations */}
      <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
        <CardContent className="p-5">
          <p className="font-semibold text-sm text-foreground mb-1 flex items-center gap-1.5"><Plug className="h-4 w-4 text-accent" /> Integrations</p>
          <p className="text-xs text-muted-foreground mb-4">The service layer is already structured for these — they switch on during onboarding.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="settings-integrations-list">
            {[
              { name: "HighLevel CRM engine", status: "Ready to connect", detail: "Contacts, pipelines, conversations, calendars, reviews", connected: false },
              { name: "WhatsApp Business", status: "Ready to connect", detail: "Primary parent messaging channel", connected: false },
              { name: "Stripe payments", status: "Payment link active", detail: highlevelPaymentsService.getDepositPaymentLink().slice(0, 44) + "…", connected: true },
              { name: "Google Business Profile", status: "Ready to connect", detail: "Review requests & reputation tracking", connected: false },
            ].map((i) => (
              <div key={i.name} className="flex items-start gap-3 bg-secondary rounded-xl px-3.5 py-3">
                {i.connected ? <CheckCircle2 className="h-4 w-4 text-teal-600 mt-0.5 shrink-0" /> : <CircleDashed className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-foreground">{i.name}</p>
                    <Badge variant="secondary" className={`text-[9px] ${i.connected ? "bg-teal-100 text-teal-800" : ""}`}>{i.status}</Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate mt-0.5">{i.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
